import type { Deal, FieldDefinition, Stage } from '@/api/types'

/**
 * Fields a stage can require before a deal enters it — mirrors backend StageRequirements. Keys:
 * `nextContactDate`, `value`, or `custom.<fieldKey>` for a deal custom field.
 */
export interface Requirement {
  key: string
  label: string
  /** Set for custom fields — drives the input the "before you move" dialog shows. */
  def?: FieldDefinition
}

const CORE: Requirement[] = [
  // the deal's next contact date is its next step's due date: requiring it means a planned next step
  { key: 'nextContactDate', label: 'Next step (a task with a date)' },
  { key: 'value', label: 'Value' },
]

/** Everything an admin can tick as required on a stage. */
export function requirableFields(dealFields: FieldDefinition[]): Requirement[] {
  return [...CORE, ...dealFields.map((d) => ({ key: `custom.${d.fieldKey}`, label: d.label, def: d }))]
}

/** "Next contact date, Settlement Date" — a stage's requirements for a summary line. */
export function requiredLabels(keys: string[], dealFields: FieldDefinition[]): string {
  const byKey = new Map(requirableFields(dealFields).map((r) => [r.key, r.label]))
  return keys
    .map((k) => byKey.get(k))
    .filter(Boolean)
    .join(', ')
}

function isBlank(v: unknown): boolean {
  return (
    v == null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0)
  )
}

/** The stage's requirements this deal doesn't meet yet, in the stage's order. */
export function missingRequirements(stage: Stage, deal: Deal, dealFields: FieldDefinition[]): Requirement[] {
  const byKey = new Map(requirableFields(dealFields).map((r) => [r.key, r]))
  return (stage.requiredFields ?? [])
    .map((key) => byKey.get(key))
    .filter((r): r is Requirement => r != null) // a requirement on a deleted field is void
    .filter((r) =>
      r.key === 'nextContactDate'
        ? !deal.nextContactDate
        : r.key === 'value'
          ? deal.value == null
          : isBlank(deal.custom?.[r.key.slice('custom.'.length)]),
    )
}
